/**
 * Lot Status Enum
 *
 * Defines valid lot statuses as returned by both the site-plan and lot-map-inventory APIs.
 * Uses singleton pattern for instance management.
 *
 * @module nativeInteractiveSiteMap/enums/lotStatus
 */

export default class LotStatus {
  static instances = {};

  /** Lot is available for purchase. */
  static get Available() {
    return this.getInstance('available');
  }

  /** Lot is coming soon — not yet released. API returns "ComingSoon" (C# enum). */
  static get ComingSoon() {
    return this.getInstance('coming-soon');
  }

  /** Lot is a model home — available to tour but not for purchase. */
  static get Model() {
    return this.getInstance('model');
  }

  /** Lot has been sold. */
  static get Sold() {
    return this.getInstance('sold');
  }

  /**
   * Lot status could not be determined, or the API returned an unrecognized value.
   * Used as the fallback by both processCommunityMap and processLotMapInventory.
   */
  static get NotFound() {
    return this.getInstance('not-found');
  }

  /**
   * Get lot status instance from a normalized API key (lowercased PascalCase).
   * @param {string} key - lowercased LotStatusType C# enum member name, e.g. 'available',
   *   'comingsoon', 'modelhomes', 'sold', 'notfound'
   * @returns {LotStatus|null} Lot status instance or null if key not recognized
   */
  static fromKey(key) {
    const map = {
      'available':  LotStatus.Available,
      'comingsoon': LotStatus.ComingSoon,
      'modelhomes': LotStatus.Model,
      'notfound':   LotStatus.NotFound,
      'sold':       LotStatus.Sold,
    };
    return map[key] ?? null;
  }

  /** @private */
  static getInstance(key) {
    if (!this.instances[key]) {
      this.instances[key] = new LotStatus(key);
    }
    return this.instances[key];
  }

  constructor(name) {
    this.name = name;
  }

  toString() {
    return this.name;
  }
}
